import { createClient, type SupportedStorage } from '@supabase/supabase-js';

const fallbackUrl = 'https://avynsiwaakwimhvkyytv.supabase.co';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || fallbackUrl;
const supabaseAnonKey =
  import.meta.env.VITE_SUPABASE_ANON_KEY || import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || '';
export const isSupabaseConfigured = Boolean(supabaseAnonKey);

function createStorageAdapter(): SupportedStorage {
  const memory = new Map<string, string>();

  const memoryStorage: SupportedStorage = {
    getItem: (key) => memory.get(key) ?? null,
    setItem: (key, value) => {
      memory.set(key, value);
    },
    removeItem: (key) => {
      memory.delete(key);
    },
  };

  if (typeof window === 'undefined') {
    return memoryStorage;
  }

  try {
    const testKey = '__traveltrok_storage_test__';
    window.localStorage.setItem(testKey, '1');
    window.localStorage.removeItem(testKey);
    return window.localStorage;
  } catch {
    return memoryStorage;
  }
}

if (!supabaseAnonKey) {
  console.warn('Missing Supabase anon key. Set VITE_SUPABASE_ANON_KEY for auth to work.');
}

const missingSupabaseConfigError = {
  message: 'Supabase non configuré (VITE_SUPABASE_ANON_KEY manquant).',
};

export const supabase = supabaseAnonKey
  ? createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
      storageKey: 'traveltrok-auth',
      storage: createStorageAdapter(),
    },
  })
  : ({
    auth: {
      onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => { } } } }),
      getSession: () => Promise.resolve({ data: { session: null }, error: null }),
      getUser: () => Promise.resolve({ data: { user: null }, error: null }),
      signInWithPassword: () => {
        return Promise.resolve({
          data: { user: null, session: null },
          error: missingSupabaseConfigError,
        });
      },
      signInWithOAuth: () => {
        return Promise.resolve({
          data: { provider: 'google', url: null },
          error: missingSupabaseConfigError,
        });
      },
      signUp: () => {
        return Promise.resolve({
          data: { user: null, session: null },
          error: missingSupabaseConfigError,
        });
      },
      signOut: () => Promise.resolve({ error: null }),
    },
    from: () => ({
      select: () => ({
        eq: () => ({
          maybeSingle: () => Promise.resolve({ data: null, error: null }),
          single: () => Promise.resolve({ data: null, error: null }),
          order: () => Promise.resolve({ data: [], error: null }),
          limit: () => Promise.resolve({ data: [], error: null }),
        }),
        maybeSingle: () => Promise.resolve({ data: null, error: null }),
        order: () => Promise.resolve({ data: [], error: null }),
        limit: () => Promise.resolve({ data: [], error: null }),
      }),
      insert: () => Promise.resolve({ data: null, error: null }),
      update: () => Promise.resolve({ data: null, error: null }),
      delete: () => Promise.resolve({ data: null, error: null }),
    }),
    storage: {
      from: () => ({
        getPublicUrl: () => ({ data: { publicUrl: '' } }),
        upload: () => Promise.resolve({ data: null, error: null }),
      }),
    },
  } as any);
